/**
 * AccountsContext
 *
 * Provides shared state management for trading accounts across desktop and mobile views.
 * Integrates with AccountService for CRUD operations and BrokerService for broker
 * dependency gating and selector population.
 *
 * Pattern follows CODING_STANDARDS:
 * - Context for truly global state; thin consumers, all logic in provider
 * - Sync refs during render body (not useEffect) for dirty tracking
 * - Service singleton imports (accountService, brokerService, syncCoordinatorService)
 * - URL hydration pattern for desktop (read on mount, write after hydration)
 * - Auto-select first item when data loads (if none selected)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { accountService } from '@/services/AccountService';
import { accountRiskService } from '@/services/AccountRiskService';
import { brokerService } from '@/services/BrokerService';
import { playbookService } from '@/services/PlaybookService';
import { strategyService } from '@/services/StrategyService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { useToastNotification } from '@enfomentis/ui';
import type { Account } from '@/types/schema/Account';
import type { AccountRisk } from '@/types/schema/AccountRisk';
import type { AccountType } from '@/types/shared/AccountType';
import type { Broker } from '@/types/schema/Broker';
import type { Playbook } from '@/types/schema/Playbook';
import type { Strategy } from '@/types/schema/Strategy';

// =============================================================================
// Form State Types
// =============================================================================

type PropFirmName = NonNullable<Account['propFirmDetails']>['firmName'];
type PropFirmAssetClass = NonNullable<Account['propFirmDetails']>['assetClass'];
type PropFirmAccountStage = NonNullable<Account['propFirmDetails']>['accountStage'];

export interface AccountFormState {
  name: string;
  description: string;
  accountType: AccountType;
  customAccountTypeName: string;
  propFirmDetails: {
    firmName: PropFirmName;
    customFirmName: string;
    assetClass: PropFirmAssetClass;
    customAssetClass: string;
    accountStage: PropFirmAccountStage;
  };
  accountNumber: string;
  brokerId: string;
  baseCurrency: string;
  feePreferences: {
    includeFeeInPnL: boolean;
    promptFeeVerification: boolean;
    trackOvernightFinancing: boolean;
    trackCurrencyConversion: boolean;
  };
  color: string;
  tags: string[];
}

const initialAccountFormState: AccountFormState = {
  name: '',
  description: '',
  accountType: 'personal',
  customAccountTypeName: '',
  propFirmDetails: {
    firmName: 'apex',
    customFirmName: '',
    assetClass: 'futures',
    customAssetClass: '',
    accountStage: 'evaluation',
  },
  accountNumber: '',
  brokerId: '',
  baseCurrency: '',
  feePreferences: {
    includeFeeInPnL: false,
    promptFeeVerification: false,
    trackOvernightFinancing: false,
    trackCurrencyConversion: false,
  },
  color: '',
  tags: [],
};

/**
 * Normalize an Account into form state, default-filling missing optional fields.
 * baseCurrency '' represents "unset / inherit from broker".
 */
export function normalizeAccountFormState(account: Account | null): AccountFormState {
  if (!account) return initialAccountFormState;

  return {
    name: account.name,
    description: account.description ?? '',
    accountType: account.accountType,
    customAccountTypeName: account.customAccountTypeName ?? '',
    propFirmDetails: {
      firmName: account.propFirmDetails?.firmName ?? 'apex',
      customFirmName: account.propFirmDetails?.customFirmName ?? '',
      assetClass: account.propFirmDetails?.assetClass ?? 'futures',
      customAssetClass: account.propFirmDetails?.customAssetClass ?? '',
      accountStage: account.propFirmDetails?.accountStage ?? 'evaluation',
    },
    accountNumber: account.accountNumber ?? '',
    brokerId: account.brokerId,
    baseCurrency: account.baseCurrency ?? '',
    feePreferences: {
      includeFeeInPnL: account.feePreferences?.includeFeeInPnL ?? false,
      promptFeeVerification: account.feePreferences?.promptFeeVerification ?? false,
      trackOvernightFinancing: account.feePreferences?.trackOvernightFinancing ?? false,
      trackCurrencyConversion: account.feePreferences?.trackCurrencyConversion ?? false,
    },
    color: account.color ?? '',
    tags: account.tags ?? [],
  };
}

// =============================================================================
// Context Types
// =============================================================================

export interface AccountsContextValue {
  // Data
  accounts: Account[];
  filteredAccounts: Account[];
  selectedAccount: Account | null;
  selectedAccountId: string | null;
  loading: boolean;

  // Broker state (for dependency gate and selector)
  activeBrokers: Broker[];
  brokersLoading: boolean;

  // Filter state
  showArchived: boolean;

  // Form state
  formState: AccountFormState;
  setFormState: React.Dispatch<React.SetStateAction<AccountFormState>>;

  // Dirty state
  isDirty: boolean;
  isSaving: boolean;
  saveSelectedAccount: () => Promise<void>;
  discardSelectedAccountChanges: () => Promise<void>;
  renameSelectedAccount: (newName: string) => Promise<boolean>;

  // Sync conflict state
  showConflictBanner: boolean;
  acceptExternalChanges: () => Promise<void>;
  keepEditing: () => void;

  // Hydration state (for URL sync)
  isHydrating: boolean;

  // Actions
  selectAccount: (id: string | null) => void;
  setShowArchived: (show: boolean) => void;

  // CRUD mutations
  createAccount: (data: Partial<Account>, startingBalance?: number) => Promise<Account>;
  updateAccount: (id: string, data: Partial<Account>) => Promise<boolean>;
  archiveAccount: () => Promise<boolean>;
  restoreAccount: () => Promise<boolean>;

  // Restore guards
  checkRestoreBlocked: () => Promise<{ blocked: false } | { blocked: true; brokerId: string; brokerName: string }>;
  restoreLinkedBrokerAndAccount: () => Promise<boolean>;

  // Risk state
  accountRisk: AccountRisk | null;
  setAccountRisk: React.Dispatch<React.SetStateAction<AccountRisk | null>>;
  riskLoading: boolean;
  riskIsDirty: boolean;
  strategies: Strategy[];
  strategyPlaybooks: Map<string, Playbook[]>;
  ensureRiskLoaded: () => Promise<void>;
}

// =============================================================================
// Context
// =============================================================================

const AccountsContext = createContext<AccountsContextValue | null>(null);

// =============================================================================
// Provider Props
// =============================================================================

interface AccountsProviderProps {
  children: React.ReactNode;
}

// =============================================================================
// Provider Implementation
// =============================================================================

export const AccountsProvider: React.FC<AccountsProviderProps> = ({ children }) => {
  const { showToast } = useToastNotification();

  // ===========================================================================
  // URL Hydration State (Desktop Only)
  // ===========================================================================

  // Hydration gate: blocks auto-selection effects until URL params are applied
  const [isHydrating, setIsHydrating] = useState(() => {
    if (typeof window === 'undefined') return false;
    const isDesktopInitial = window.matchMedia('(min-width: 768px)').matches;
    if (!isDesktopInitial) return false;
    const params = new URLSearchParams(window.location.search);
    return params.has('accountId');
  });

  // Capture initial URL params on mount (before any effects can modify URL)
  const initialUrlSelectionRef = useRef<string | null>(null);
  if (initialUrlSelectionRef.current === null && typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    initialUrlSelectionRef.current = params.get('accountId');
  }

  // ===========================================================================
  // Data State
  // ===========================================================================

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [activeBrokers, setActiveBrokers] = useState<Broker[]>([]);
  const [brokersLoading, setBrokersLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formState, setFormState] = useState<AccountFormState>(initialAccountFormState);

  // ===========================================================================
  // Risk State
  // ===========================================================================

  const [accountRisk, setAccountRisk] = useState<AccountRisk | null>(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [strategyPlaybooks, setStrategyPlaybooks] = useState<Map<string, Playbook[]>>(new Map());

  // ===========================================================================
  // Dirty State
  // ===========================================================================

  const baselineRef = useRef<string>(JSON.stringify(initialAccountFormState));
  const isDirtyRef = useRef(false);

  // Risk dirty state
  const riskBaselineRef = useRef<string>('null');
  const riskIsDirtyRef = useRef(false);
  const riskLoadedForAccountRef = useRef<string | null>(null);
  const strategiesLoadedRef = useRef(false);

  // ===========================================================================
  // Sync Conflict State
  // ===========================================================================

  const [hasQueuedUpdate, setHasQueuedUpdate] = useState(false);
  const [dismissedExternalUpdate, setDismissedExternalUpdate] = useState(false);

  // Risk sync conflict state
  const [hasQueuedRiskUpdate, setHasQueuedRiskUpdate] = useState(false);
  const [dismissedExternalRiskUpdate, setDismissedExternalRiskUpdate] = useState(false);

  // ===========================================================================
  // Refs for Sync Callbacks (sync during render body, not useEffect)
  // ===========================================================================

  const selectedAccountIdRef = useRef(selectedAccountId);
  const prevSyncedAccountIdRef = useRef<string | null>(null);
  const accountRiskIdRef = useRef<string | null>(null);

  // ===========================================================================
  // Data Loading Functions
  // ===========================================================================

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const allAccounts = await accountService.getAll();
      setAccounts(allAccounts);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadActiveBrokers = useCallback(async () => {
    try {
      setBrokersLoading(true);
      const brokers = await brokerService.getActiveBrokers();
      setActiveBrokers(brokers);
    } catch (error) {
      console.error('Failed to load active brokers:', error);
    } finally {
      setBrokersLoading(false);
    }
  }, []);

  // ===========================================================================
  // Risk Data Loading (Lazy — triggered by ensureRiskLoaded on Risk tab)
  // ===========================================================================

  const loadAccountRisk = useCallback(async (accountId: string): Promise<void> => {
    setRiskLoading(true);
    try {
      const activeRisks = await accountRiskService.getActiveByAccountId(accountId);

      // Sort ascending by createdAt, use oldest as canonical
      activeRisks.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      const [firstRisk] = activeRisks;

      let canonical: AccountRisk;
      if (firstRisk) {
        canonical = firstRisk;
      } else {
        // Auto-create default AccountRisk with one "Standard" profile at 1%
        const profileId = crypto.randomUUID();
        canonical = await accountRiskService.add({
          accountId,
          name: 'Risk Configuration',
          status: 'active',
          riskProfiles: [
            {
              id: profileId,
              name: 'Standard',
              status: 'active',
              order: 0,
              accountDefault: { type: 'percentage', value: 0.01 },
            },
          ],
          defaultProfileId: profileId,
        });
        syncCoordinatorService.notifyChange('account_risks', canonical.id);
      }

      setAccountRisk(canonical);
      riskBaselineRef.current = JSON.stringify(canonical);
      riskLoadedForAccountRef.current = accountId;
    } catch (error) {
      console.error('Failed to load account risk:', error);
    } finally {
      setRiskLoading(false);
    }
  }, []);

  const loadStrategiesAndPlaybooks = useCallback(async (): Promise<void> => {
    try {
      const activeStrategies = await strategyService.getActiveStrategies();
      setStrategies(activeStrategies);

      const playbookMap = new Map<string, Playbook[]>();
      await Promise.all(
        activeStrategies.map(async (strategy) => {
          const playbooks = await playbookService.getByStrategyId(strategy.id);
          playbookMap.set(strategy.id, playbooks);
        })
      );
      setStrategyPlaybooks(playbookMap);
      strategiesLoadedRef.current = true;
    } catch (error) {
      console.error('Failed to load strategies and playbooks:', error);
    }
  }, []);

  const ensureRiskLoaded = useCallback(async (): Promise<void> => {
    if (!selectedAccountId) return;
    if (riskLoadedForAccountRef.current === selectedAccountId) return;

    const promises: Promise<void>[] = [loadAccountRisk(selectedAccountId)];
    if (!strategiesLoadedRef.current) {
      promises.push(loadStrategiesAndPlaybooks());
    }
    await Promise.all(promises);
  }, [selectedAccountId, loadAccountRisk, loadStrategiesAndPlaybooks]);

  // ===========================================================================
  // Initial Data Load
  // ===========================================================================

  useEffect(() => {
    loadAccounts();
    loadActiveBrokers();
  }, [loadAccounts, loadActiveBrokers]);

  // ===========================================================================
  // Sync Subscription (Cross-Tab)
  // ===========================================================================

  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('accounts', (payload) => {
      if (isDirtyRef.current && payload.recordId === selectedAccountIdRef.current) {
        // External change to selected account while dirty — queue update
        setHasQueuedUpdate(true);
        setDismissedExternalUpdate(false);
      } else {
        // Either not dirty, or change is for a different account — reload list
        // The Sync Local State effect handles formState sync when not dirty
        loadAccounts();
      }
    });

    return () => unsubscribe();
  }, [loadAccounts]);

  // ===========================================================================
  // Risk Sync Subscription (Cross-Tab)
  // ===========================================================================

  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('account_risks', (payload) => {
      // Guard: ignore if risk data not loaded yet
      if (riskLoadedForAccountRef.current === null) return;

      if (riskIsDirtyRef.current && payload.recordId === accountRiskIdRef.current) {
        // External change to loaded risk entity while dirty — queue update
        setHasQueuedRiskUpdate(true);
        setDismissedExternalRiskUpdate(false);
      } else if (riskLoadedForAccountRef.current) {
        // Not dirty or different record — reload risk data
        loadAccountRisk(riskLoadedForAccountRef.current);
      }
    });

    return () => unsubscribe();
  }, [loadAccountRisk]);

  // ===========================================================================
  // URL Hydration Effect (Desktop Only)
  // ===========================================================================

  useEffect(() => {
    // Only run while hydrating
    if (!isHydrating) return;

    // Wait for data to load
    if (loading) return;

    const urlAccountId = initialUrlSelectionRef.current;

    // If no URL param, complete hydration immediately
    if (!urlAccountId) {
      setIsHydrating(false);
      return;
    }

    // Apply account selection from URL
    const account = accounts.find((a) => a.id === urlAccountId);
    if (account) {
      // Select the account if not already selected
      if (selectedAccountId !== urlAccountId) {
        setSelectedAccountId(urlAccountId);
      }
    }
    // Invalid account ID or selection complete - finish hydration
    setIsHydrating(false);
  }, [isHydrating, loading, accounts, selectedAccountId]);

  // ===========================================================================
  // Auto-Selection Effect
  // ===========================================================================

  useEffect(() => {
    // Gate: skip during URL hydration to avoid race conditions
    if (isHydrating) return;

    // Auto-select first active account when data loads (if none selected).
    // Selection continuity: selectedAccountId persists across reloads —
    // this only fires when selectedAccountId is null (initial load or
    // after selected account is removed from the list).
    const firstActive = accounts.find((a) => a.status === 'active');
    if (!loading && firstActive && !selectedAccountId) {
      setSelectedAccountId(firstActive.id);
    }
  }, [loading, accounts, selectedAccountId, isHydrating]);

  // ===========================================================================
  // Selection Reconciliation
  // ===========================================================================

  useEffect(() => {
    // Gate: skip during URL hydration to avoid race conditions
    if (isHydrating) return;
    if (loading) return;
    if (!selectedAccountId) return;

    // Clear stale selection if the account no longer exists in the full dataset.
    // Uses `accounts` (not `filteredAccounts`) — reconciliation is about data
    // validity, not filter visibility. An archived-but-existing account is valid
    // when showArchived is on.
    const exists = accounts.some((a) => a.id === selectedAccountId);
    if (!exists) {
      setSelectedAccountId(null);
    }
  }, [selectedAccountId, accounts, loading, isHydrating]);

  // ===========================================================================
  // Derived State
  // ===========================================================================

  const selectedAccount = useMemo(() => {
    if (!selectedAccountId) return null;
    return accounts.find((a) => a.id === selectedAccountId) ?? null;
  }, [accounts, selectedAccountId]);

  const filteredAccounts = useMemo(() => {
    return showArchived
      ? accounts
      : accounts.filter((a) => a.status !== 'archived');
  }, [accounts, showArchived]);

  // ===========================================================================
  // Computed Dirty State (render body per CODING_STANDARDS)
  // ===========================================================================

  const formJson = JSON.stringify(formState);
  const accountIsDirty = formJson !== baselineRef.current;

  const riskJson = JSON.stringify(accountRisk);
  const riskIsDirty = riskJson !== riskBaselineRef.current;

  const isDirty = accountIsDirty || riskIsDirty;

  const showConflictBanner =
    (hasQueuedUpdate && accountIsDirty && !dismissedExternalUpdate) ||
    (hasQueuedRiskUpdate && riskIsDirty && !dismissedExternalRiskUpdate);

  // Sync refs during render body so sync callbacks can read latest state
  isDirtyRef.current = accountIsDirty;
  riskIsDirtyRef.current = riskIsDirty;
  selectedAccountIdRef.current = selectedAccountId;
  accountRiskIdRef.current = accountRisk?.id ?? null;

  // ===========================================================================
  // Sync Local State with Selected Account
  // ===========================================================================

  useEffect(() => {
    // Gate: skip during URL hydration to avoid overwriting URL-selected account
    if (isHydrating) return;

    const isIdChange = selectedAccount?.id !== prevSyncedAccountIdRef.current;
    prevSyncedAccountIdRef.current = selectedAccount?.id ?? null;

    if (selectedAccount) {
      // Always sync on ID change (selection change)
      // Only sync on data change if not dirty (cross-tab update)
      if (isIdChange || !isDirtyRef.current) {
        const normalized = normalizeAccountFormState(selectedAccount);
        setFormState(normalized);
        baselineRef.current = JSON.stringify(normalized);
      }

      // Reset conflict state and risk state on ID change
      if (isIdChange) {
        setHasQueuedUpdate(false);
        setDismissedExternalUpdate(false);

        // Reset risk state (lazy — will reload when Risk tab calls ensureRiskLoaded)
        setAccountRisk(null);
        riskBaselineRef.current = 'null';
        riskLoadedForAccountRef.current = null;
        setHasQueuedRiskUpdate(false);
        setDismissedExternalRiskUpdate(false);
      }
    } else {
      setFormState(initialAccountFormState);
      baselineRef.current = JSON.stringify(initialAccountFormState);

      // Also reset risk state when no account selected
      setAccountRisk(null);
      riskBaselineRef.current = 'null';
      riskLoadedForAccountRef.current = null;
      setHasQueuedRiskUpdate(false);
      setDismissedExternalRiskUpdate(false);
    }
  }, [selectedAccount, isHydrating]);

  // ===========================================================================
  // Actions
  // ===========================================================================

  const selectAccount = useCallback((id: string | null) => {
    setSelectedAccountId(id);
  }, []);

  // ===========================================================================
  // CRUD Mutations
  // ===========================================================================

  const createAccount = useCallback(async (
    data: Partial<Account>,
    startingBalance?: number,
  ): Promise<Account> => {
    setIsSaving(true);
    try {
      const newAccount = await accountService.createWithStartingBalance(data, startingBalance);
      syncCoordinatorService.notifyChange('accounts', newAccount.id);
      showToast({
        title: 'Account Created',
        description: `"${newAccount.name}" has been created`,
        color: 'success',
      });
      await loadAccounts();
      return newAccount;
    } catch (error) {
      console.error('Failed to create account:', error);
      showToast({
        title: 'Create Failed',
        description: 'Could not create account. Please try again.',
        color: 'danger',
      });
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [showToast, loadAccounts]);

  const updateAccount = useCallback(async (
    id: string,
    data: Partial<Account>,
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      const updated = await accountService.update(id, data);
      syncCoordinatorService.notifyChange('accounts', id);
      showToast({
        title: 'Account Saved',
        description: `"${updated.name}" updated`,
        color: 'success',
      });
      await loadAccounts();
      return true;
    } catch (error) {
      console.error('Failed to save account:', error);
      showToast({
        title: 'Save Failed',
        description: 'Could not save account. Please try again.',
        color: 'danger',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [showToast, loadAccounts]);

  const archiveAccount = useCallback(async (): Promise<boolean> => {
    if (!selectedAccount) return false;

    setIsSaving(true);
    try {
      await accountService.update(selectedAccount.id, { status: 'archived' });
      syncCoordinatorService.notifyChange('accounts', selectedAccount.id);
      showToast({
        title: 'Account Archived',
        description: `"${selectedAccount.name}" has been archived`,
        color: 'success',
      });
      await loadAccounts();
      // Clear selection — the auto-select effect picks the next active account
      setSelectedAccountId(null);
      return true;
    } catch (error) {
      console.error('Failed to archive account:', error);
      showToast({
        title: 'Archive Failed',
        description: 'Could not archive account. Please try again.',
        color: 'danger',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [selectedAccount, showToast, loadAccounts]);

  const restoreAccount = useCallback(async (): Promise<boolean> => {
    if (!selectedAccount) return false;

    setIsSaving(true);
    try {
      await accountService.update(selectedAccount.id, { status: 'active' });
      syncCoordinatorService.notifyChange('accounts', selectedAccount.id);
      showToast({
        title: 'Account Restored',
        description: `"${selectedAccount.name}" is now active`,
        color: 'success',
      });
      await loadAccounts();
      return true;
    } catch (error) {
      console.error('Failed to restore account:', error);
      showToast({
        title: 'Restore Failed',
        description: 'Could not restore account. Please try again.',
        color: 'danger',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [selectedAccount, showToast, loadAccounts]);

  const checkRestoreBlocked = useCallback(async (): Promise<
    { blocked: false } | { blocked: true; brokerId: string; brokerName: string }
  > => {
    if (!selectedAccount) return { blocked: false };
    try {
      const broker = await brokerService.get(selectedAccount.brokerId);
      if (broker && broker.status === 'archived') {
        return { blocked: true, brokerId: broker.id, brokerName: broker.name };
      }
    } catch (error) {
      console.error('Failed to check linked broker status:', error);
    }
    return { blocked: false };
  }, [selectedAccount]);

  const restoreLinkedBrokerAndAccount = useCallback(async (): Promise<boolean> => {
    if (!selectedAccount) return false;

    setIsSaving(true);
    try {
      // Step 1: Restore the linked broker
      await brokerService.update(selectedAccount.brokerId, { status: 'active' });
      syncCoordinatorService.notifyChange('brokers', selectedAccount.brokerId);
      await loadActiveBrokers();
    } catch (error) {
      console.error('Failed to restore linked broker:', error);
      showToast({
        title: 'Restore Failed',
        description: 'Could not restore the linked broker. Please try again.',
        color: 'danger',
      });
      setIsSaving(false);
      return false;
    }

    try {
      // Step 2: Restore the account
      await accountService.update(selectedAccount.id, { status: 'active' });
      syncCoordinatorService.notifyChange('accounts', selectedAccount.id);
      showToast({
        title: 'Account & Broker Restored',
        description: `"${selectedAccount.name}" and its linked broker are now active`,
        color: 'success',
      });
      await loadAccounts();
      return true;
    } catch (error) {
      console.error('Failed to restore account (broker was restored):', error);
      showToast({
        title: 'Partial Restore',
        description: 'Broker restored, but account restore failed. Please try restoring the account again.',
        color: 'warning',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [selectedAccount, showToast, loadAccounts, loadActiveBrokers]);

  // ===========================================================================
  // Selected Account Save & Discard
  // ===========================================================================

  const saveSelectedAccount = useCallback(async (): Promise<void> => {
    if (!selectedAccount || (!accountIsDirty && !riskIsDirty) || isSaving) return;

    setIsSaving(true);
    let accountSaveOk = true;
    let riskSaveOk = true;

    try {
      // --- Account save (if account fields dirty) ---
      if (accountIsDirty) {
        try {
          const data: Partial<Account> = {
            name: formState.name,
            accountType: formState.accountType,
            brokerId: formState.brokerId,
            feePreferences: formState.feePreferences,
            ...(formState.description ? { description: formState.description } : {}),
            ...(formState.baseCurrency ? { baseCurrency: formState.baseCurrency } : {}),
            ...(formState.accountNumber ? { accountNumber: formState.accountNumber } : {}),
            ...(formState.color ? { color: formState.color } : {}),
            ...(formState.tags.length > 0 ? { tags: formState.tags } : {}),
            ...(formState.accountType === 'other' && formState.customAccountTypeName
              ? { customAccountTypeName: formState.customAccountTypeName }
              : {}),
            ...(formState.accountType === 'prop'
              ? {
                  propFirmDetails: {
                    firmName: formState.propFirmDetails.firmName,
                    assetClass: formState.propFirmDetails.assetClass,
                    accountStage: formState.propFirmDetails.accountStage,
                    ...(formState.propFirmDetails.customFirmName
                      ? { customFirmName: formState.propFirmDetails.customFirmName }
                      : {}),
                    ...(formState.propFirmDetails.customAssetClass
                      ? { customAssetClass: formState.propFirmDetails.customAssetClass }
                      : {}),
                  },
                }
              : {}),
          };

          await accountService.update(selectedAccount.id, data);
          syncCoordinatorService.notifyChange('accounts', selectedAccount.id);
          await loadAccounts();
          baselineRef.current = JSON.stringify(formState);
          setHasQueuedUpdate(false);
        } catch (error) {
          console.error('Failed to save account:', error);
          accountSaveOk = false;
        }
      }

      // --- Risk save (if risk fields dirty and risk data loaded) ---
      if (riskIsDirty && accountRisk) {
        try {
          const riskPayload: Partial<AccountRisk> = {
            riskProfiles: accountRisk.riskProfiles,
            ...(accountRisk.defaultProfileId
              ? { defaultProfileId: accountRisk.defaultProfileId }
              : {}),
            ...(accountRisk.description
              ? { description: accountRisk.description }
              : {}),
          };

          await accountRiskService.update(accountRisk.id, riskPayload);
          syncCoordinatorService.notifyChange('account_risks', accountRisk.id);
          riskBaselineRef.current = JSON.stringify(accountRisk);
          setHasQueuedRiskUpdate(false);
        } catch (error) {
          console.error('Failed to save risk settings:', error);
          riskSaveOk = false;
        }
      }

      // --- Toast contract for combined results ---
      if (accountSaveOk && riskSaveOk) {
        showToast({
          title: 'Account Saved',
          description: `"${selectedAccount.name}" updated`,
          color: 'success',
        });
      } else if (accountSaveOk && !riskSaveOk) {
        showToast({
          title: 'Partial Save',
          description: 'Risk settings could not be saved. Your account changes were saved.',
          color: 'warning',
        });
      } else if (!accountSaveOk && riskSaveOk) {
        showToast({
          title: 'Partial Save',
          description: 'Account changes could not be saved. Your risk settings were saved.',
          color: 'warning',
        });
      } else {
        showToast({
          title: 'Save Failed',
          description: 'Changes could not be saved. Please try again.',
          color: 'danger',
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [selectedAccount, accountIsDirty, riskIsDirty, isSaving, formState, accountRisk, showToast, loadAccounts]);

  const discardSelectedAccountChanges = useCallback(async (): Promise<void> => {
    // Restore account form state from baseline
    const baseline = JSON.parse(baselineRef.current) as AccountFormState;
    setFormState(baseline);

    // Restore risk state from baseline (if risk was loaded)
    if (riskIsDirtyRef.current) {
      const riskBaseline = JSON.parse(riskBaselineRef.current) as AccountRisk | null;
      setAccountRisk(riskBaseline);
    }

    // If there are queued external updates, apply them now
    if (hasQueuedUpdate) {
      setHasQueuedUpdate(false);
      setDismissedExternalUpdate(false);
      await loadAccounts();
    }
    if (hasQueuedRiskUpdate && riskLoadedForAccountRef.current) {
      setHasQueuedRiskUpdate(false);
      setDismissedExternalRiskUpdate(false);
      await loadAccountRisk(riskLoadedForAccountRef.current);
    }
  }, [hasQueuedUpdate, hasQueuedRiskUpdate, loadAccounts, loadAccountRisk]);

  const acceptExternalChanges = useCallback(async (): Promise<void> => {
    if (!selectedAccountId) return;

    // Reload accounts list
    await loadAccounts();

    // Fetch fresh account and apply fields directly
    const freshAccount = await accountService.get(selectedAccountId);
    if (freshAccount) {
      const normalized = normalizeAccountFormState(freshAccount);
      setFormState(normalized);
      baselineRef.current = JSON.stringify(normalized);
    } else {
      // Account was deleted — notify user and clear selection
      showToast({
        title: 'Account No Longer Exists',
        description: 'This account was deleted in another tab.',
        color: 'warning',
      });
      selectAccount(null);
    }

    // Clear account conflict state
    setHasQueuedUpdate(false);
    setDismissedExternalUpdate(false);

    // Also reload risk data if it was loaded
    if (riskLoadedForAccountRef.current) {
      await loadAccountRisk(riskLoadedForAccountRef.current);
      setHasQueuedRiskUpdate(false);
      setDismissedExternalRiskUpdate(false);
    }
  }, [loadAccounts, loadAccountRisk, selectedAccountId, showToast, selectAccount]);

  const keepEditing = useCallback(() => {
    setDismissedExternalUpdate(true);
    setDismissedExternalRiskUpdate(true);
  }, []);

  const renameSelectedAccount = useCallback(async (newName: string): Promise<boolean> => {
    if (!selectedAccount) return false;

    const success = await updateAccount(selectedAccount.id, { name: newName });
    if (success) {
      setFormState((prev) => ({ ...prev, name: newName }));
      const currentBaseline = JSON.parse(baselineRef.current) as AccountFormState;
      baselineRef.current = JSON.stringify({ ...currentBaseline, name: newName });
    }
    return success;
  }, [selectedAccount, updateAccount]);

  // ===========================================================================
  // Context Value (Memoized)
  // ===========================================================================

  const value = useMemo<AccountsContextValue>(
    () => ({
      accounts,
      filteredAccounts,
      selectedAccount,
      selectedAccountId,
      loading,
      activeBrokers,
      brokersLoading,
      formState,
      setFormState,
      showArchived,
      isDirty,
      isSaving,
      isHydrating,
      showConflictBanner,
      saveSelectedAccount,
      discardSelectedAccountChanges,
      renameSelectedAccount,
      acceptExternalChanges,
      keepEditing,
      selectAccount,
      setShowArchived,
      createAccount,
      updateAccount,
      archiveAccount,
      restoreAccount,
      checkRestoreBlocked,
      restoreLinkedBrokerAndAccount,
      accountRisk,
      setAccountRisk,
      riskLoading,
      riskIsDirty,
      strategies,
      strategyPlaybooks,
      ensureRiskLoaded,
    }),
    [
      accounts,
      filteredAccounts,
      selectedAccount,
      selectedAccountId,
      loading,
      activeBrokers,
      brokersLoading,
      formState,
      setFormState,
      showArchived,
      isDirty,
      isSaving,
      isHydrating,
      showConflictBanner,
      saveSelectedAccount,
      discardSelectedAccountChanges,
      renameSelectedAccount,
      acceptExternalChanges,
      keepEditing,
      selectAccount,
      setShowArchived,
      createAccount,
      updateAccount,
      archiveAccount,
      restoreAccount,
      checkRestoreBlocked,
      restoreLinkedBrokerAndAccount,
      accountRisk,
      setAccountRisk,
      riskLoading,
      riskIsDirty,
      strategies,
      strategyPlaybooks,
      ensureRiskLoaded,
    ]
  );

  return (
    <AccountsContext.Provider value={value}>
      {children}
    </AccountsContext.Provider>
  );
};

// =============================================================================
// Consumer Hook
// =============================================================================

/**
 * Hook to consume the AccountsContext.
 * Must be used within an AccountsProvider.
 *
 * @throws Error if used outside of AccountsProvider
 */
export function useAccounts(): AccountsContextValue {
  const context = useContext(AccountsContext);
  if (!context) {
    throw new Error(
      'useAccounts must be used within an AccountsProvider. ' +
        'Wrap your component tree with <AccountsProvider>.'
    );
  }
  return context;
}
